import { createSelector } from '@reduxjs/toolkit';

import { pruneInactiveNodes } from '@/shared/filters/filter-tree';
import type { RootState } from '@/store/root-reducer';

import { countActiveRules } from '../../_shared/domain/filter-tree';
import { mergeSelection } from '../../_shared/domain/merge-selection';
import type { MethodScopeData } from '../../_shared/domain/method-scope';
import { validateDraft } from '../domain/edit-draft.validation';

const selectEditList = (state: RootState) => state.editList;

export const selectDraft = (state: RootState) => selectEditList(state).draft;
export const selectListId = (state: RootState) => selectEditList(state).draft.listId;
/** Server-side name; stable while the draft name is being edited. */
export const selectLoadedName = (state: RootState) => selectEditList(state).loadedName;
export const selectLicenceId = (state: RootState) => selectEditList(state).draft.licenceId;
export const selectLoadedLicenceId = (state: RootState) => selectEditList(state).loadedLicenceId;

/** Pending licence transfer (#520): the loaded and chosen licences, or null when they agree. */
export const selectLicenceTransfer = createSelector(
  [
    (state: RootState) => selectEditList(state).loadedLicenceId,
    (state: RootState) => selectEditList(state).loadedLicenceName,
    (state: RootState) => selectEditList(state).draft.licenceId,
    (state: RootState) => selectEditList(state).draft.licenceName,
  ],
  (fromId, fromName, toId, toName) =>
    fromId && toId && fromId !== toId ? { fromId, fromName, toId, toName } : null,
);
export const selectScope = (state: RootState) => selectEditList(state).draft.scope;
export const selectStatus = (state: RootState) => selectEditList(state).draft.status;
export const selectMethods = (state: RootState) => selectEditList(state).draft.methods;
export const selectStep = (state: RootState) => selectEditList(state).step;
export const selectLoadStatus = (state: RootState) => selectEditList(state).loadStatus;
export const selectIsLoaded = (state: RootState) => selectEditList(state).loadStatus === 'loaded';
export const selectSaveStatus = (state: RootState) => selectEditList(state).saveStatus;
export const selectSavePhase = (state: RootState) => selectEditList(state).savePhase;
export const selectError = (state: RootState) => selectEditList(state).error;
export const selectValidationErrors = (state: RootState) => selectEditList(state).validationErrors;

export const selectIsSaving = (state: RootState) => selectEditList(state).saveStatus === 'saving';

// ── Delete ──
export const selectDeleting = (state: RootState) => selectEditList(state).deleting;
export const selectDeleted = (state: RootState) => selectEditList(state).deleted;

/**
 * Derived: the client-facing description's message, live (#718). Next is
 * disabled while the draft is invalid, so the saga's submit-time errors never
 * show it; the field reads it from here instead.
 */
export const selectClientScopeDescriptionError = createSelector(
  selectDraft,
  (draft) => validateDraft(draft).clientScopeDescription,
);

/** Derived: is the current draft valid? Memoised on the draft reference. */
export const selectIsDraftValid = createSelector(
  selectDraft,
  (draft) => Object.keys(validateDraft(draft)).length === 0,
);

// ── Selection ──
export const selectExcludeDuplicates = (state: RootState) =>
  selectEditList(state).excludeDuplicates;
export const selectImportedProfileIds = (state: RootState) =>
  selectEditList(state).importedProfileIds;
export const selectImportedListName = (state: RootState) => selectEditList(state).importedListName;
export const selectFileImportData = (state: RootState) => selectEditList(state).fileImportData;
export const selectFileUpload = (state: RootState) => selectEditList(state).fileUpload;
export const selectEnrichNew = (state: RootState) => selectEditList(state).enrichNew;
export const selectEnrichStale = (state: RootState) => selectEditList(state).enrichStale;

// ── Filters ──
export const selectRootGroup = (state: RootState) => selectEditList(state).filters.rootGroup;

/** Pruned filter tree sent to the server. `null` = no active filters. The
 * 500ms debounce lives in the saga, NOT here. Memoised on the tree reference. */
export const selectServerFilters = createSelector(selectRootGroup, (rootGroup) => {
  const pruned = pruneInactiveNodes(rootGroup);
  return pruned.children.length > 0 ? pruned : null;
});

/** Number of active rules across the tree (feeds the filter-bar badge). */
export const selectActiveRuleCount = createSelector(selectRootGroup, (rootGroup) =>
  countActiveRules(rootGroup),
);

// ── Profile table ──
export const selectProfileRows = (state: RootState) => selectEditList(state).profiles.rows;
export const selectProfileTotal = (state: RootState) => selectEditList(state).profiles.total;
export const selectProfilePage = (state: RootState) => selectEditList(state).profiles.page;
export const selectProfilePageSize = (state: RootState) => selectEditList(state).profiles.pageSize;
export const selectProfilesLoading = (state: RootState) => selectEditList(state).profiles.loading;

// ── Async results ──
export const selectDuplicateCount = (state: RootState) => selectEditList(state).duplicateCount;
export const selectDuplicateLoading = (state: RootState) => selectEditList(state).duplicateLoading;
export const selectEnrichment = (state: RootState) => selectEditList(state).enrichment;
export const selectEnrichmentLoading = (state: RootState) =>
  selectEditList(state).enrichmentLoading;
export const selectNlSearching = (state: RootState) => selectEditList(state).nlSearching;

/**
 * Smart-merge of filter-derived + imported ids (the "Select profiles" summary).
 *
 * Each source is gated on its own method, so the recap counts exactly what
 * `buildSavePlan` will send. Without that gate the summary quietly reported
 * (and the save quietly shipped) profiles from a method the user had unselected
 * (docs/product-issues.md PI-40).
 */
export const selectImportStats = createSelector(
  selectMethods,
  selectEnrichment,
  selectImportedProfileIds,
  (methods, enrichment, importedIds) =>
    mergeSelection(
      methods.includes('select') ? enrichment.filterOnlyIds : [],
      methods.includes('import') ? importedIds : [],
    ),
);

// ── "Import from existing list" step ──
const selectListImport = (state: RootState) => selectEditList(state).listImport;
export const selectSourceOrigin = (state: RootState) => selectListImport(state).origin;
export const selectSourceLicenceId = (state: RootState) => selectListImport(state).sourceLicenceId;
export const selectSourceLists = (state: RootState) => selectListImport(state).sourceLists;
export const selectSourceListsLoading = (state: RootState) =>
  selectListImport(state).sourceListsLoading;
export const selectIsImporting = (state: RootState) => selectListImport(state).importing;
export const selectRawImportedIds = (state: RootState) => selectListImport(state).rawImportedIds;
export const selectImportExcludeDuplicates = (state: RootState) =>
  selectListImport(state).excludeDuplicates;

/** Duplicates found on the last import; derived, never stored. */
export const selectImportDuplicateCount = createSelector(
  (state: RootState) => selectListImport(state).duplicateIds,
  (ids) => ids.length,
);

// ── Imported-profiles preview table ──
export const selectImportPreviewRows = (state: RootState) => selectListImport(state).preview.rows;
export const selectImportPreviewTotal = (state: RootState) => selectListImport(state).preview.total;
export const selectImportPreviewLoading = (state: RootState) =>
  selectListImport(state).preview.loading;
export const selectImportPreviewPage = (state: RootState) => selectListImport(state).preview.page;
export const selectImportPreviewPageSize = (state: RootState) =>
  selectListImport(state).preview.pageSize;

// ── Licence dropdown of the import step ──
export const selectLicences = (state: RootState) => selectEditList(state).licences;
export const selectLicencesLoading = (state: RootState) => selectEditList(state).licencesLoading;

// ── Method ownership ──

/**
 * Which methods currently hold data. Unselecting one discards what it owned, so
 * the details step reads this to warn before the loss rather than after.
 *
 * Declared last: it composes selectors defined further up the file.
 */
export const selectMethodScopeData = createSelector(
  selectFileUpload,
  selectFileImportData,
  selectRawImportedIds,
  selectActiveRuleCount,
  (fileUpload, fileImportData, rawImportedIds, activeRuleCount): MethodScopeData => ({
    file: fileImportData !== null || fileUpload.fileName !== null,
    import: rawImportedIds.length > 0,
    select: activeRuleCount > 0,
  }),
);
